import { useCallback, useRef, useState } from 'react';
import { LcaResultModel } from 'types/types';
import { useUpsertEpdLcaResult } from 'services/api/mutationsLcaResults';

type UseGridSyncOptions = {
  epdVersionId: string;
  initialRows: LcaResultModel[];
};

type UseGridSyncResult = {
  localRows: LcaResultModel[];
  handleRowsChange: (updatedRows: LcaResultModel[]) => void;
  isSyncing: boolean;
  syncError: string | null;
};

const DEBOUNCE_MS = 300;

/**
 * Hook that manages local ReactGrid state with debounced sync to backend
 * via the existing useUpsertEpdLcaResult mutation (which already does optimistic updates + query invalidation).
 */
export function useGridSync({ epdVersionId, initialRows }: UseGridSyncOptions): UseGridSyncResult {
  const [localRows, setLocalRows] = useState<LcaResultModel[]>(initialRows);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingChangesRef = useRef<Map<string, LcaResultModel>>(new Map());

  const upsertMutation = useUpsertEpdLcaResult(epdVersionId);

  // When source rows change externally (query refetch), update local state
  // This is handled by re-mounting with new initialRows or via useEffect in parent

  const flushPendingChanges = useCallback(() => {
    const pending = pendingChangesRef.current;
    if (pending.size === 0) return;

    setIsSyncing(true);
    setSyncError(null);

    const entries = Array.from(pending.values());
    pendingChangesRef.current = new Map();

    // Send each changed row through the existing upsert mutation
    // (the existing mutation handles optimistic update + serial scoping)
    for (const row of entries) {
      upsertMutation.mutate(row, {
        onError: () => {
          setSyncError('Failed to save changes. Please try again.');
          setIsSyncing(false);
        },
        onSettled: () => {
          setIsSyncing(false);
        },
      });
    }
  }, [upsertMutation]);

  const handleRowsChange = useCallback(
    (updatedRows: LcaResultModel[]) => {
      setLocalRows(updatedRows);

      // Track which rows changed
      for (const row of updatedRows) {
        const originalRow = localRows.find((r) => r.indicatorId === row.indicatorId);
        if (originalRow && JSON.stringify(originalRow) !== JSON.stringify(row)) {
          pendingChangesRef.current.set(row.indicatorId || '', row);
        }
      }

      // Debounce the sync
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(flushPendingChanges, DEBOUNCE_MS);
    },
    [localRows, flushPendingChanges]
  );

  return {
    localRows,
    handleRowsChange,
    isSyncing,
    syncError,
  };
}
